import React, { useState, useContext, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Row, Col } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import moduleConfig from "./moduleConfig";
import { BreadcrumbContext } from "../../../contexts/BreadcrumbContext";
import DashboardLayout from "../../UI/layouts/DashboardLayout";
import { ContainerLoading } from "../../UI/loading/ContainerLoading";
import BlockErrorMessage from "../../UI/errorMessages/BlockErrorMessage";
import ModuleHeader from "../../UI/modules/views/partials/common/ModuleHeader";
import { isEmpty } from "../../../helpers/CommonHelper";
import FormErrorMessage from "../../UI/errorMessages/FormErrorMessage";
import CMSFormInput from "../../UI/forms/CMSFormInput";
import CMSFormSelect from "../../UI/forms/CMSFormSelect";
import CMSFormRichText from "../../UI/forms/CMSFormRichText";
import CMSFormImage from "../../UI/forms/CMSFormImage";
import {
  updateInitialState,
  updateReducer,
} from "../../../reducers/module/updateReducer";
import {
  loadItemGeneral,
  onSubmitGeneral,
} from "../../../helpers/ModuleHelperFunctions";
import CMSFormColor from "../../UI/forms/CMSFormColor";
import CMSFormImageWithCrop from "../../UI/forms/CMSFormImageWithCrop";

const MealUpdate = () => {
  const [updateState, updateDispatch] = useReducer(
    updateReducer,
    updateInitialState
  );
  const [formInitialValues, setFormInitialValues] = useState({});
  const { setBreadcrumb } = useContext(BreadcrumbContext);

  let { id } = useParams();
  let navigate = useNavigate();

  const loadItem = async () => {
    await loadItemGeneral(id, `${moduleConfig.url}/${id}`, updateDispatch);
  };
  useEffect(() => {
    loadItem();
    setBreadcrumb([
      moduleConfig.breadcrumbs.listing,
      // moduleConfig.breadcrumbs.details,
      moduleConfig.breadcrumbs.update,
    ]);
  }, [id]);

  useEffect(() => {
    if (updateState.data.item && updateState.data.item._id) {
      setFormInitialValues(updateState.data.item);
    }
  }, [updateState.data]);
  let imageRequirement = {
    dimension: {
      width: 500,
      height: 500,
    },
    allowedExtensions: ["image/png", "image/jpg", "image/jpeg"],
    size: 1024 * 1024 * 3,
  };
  return (
    <DashboardLayout>
      {updateState.status === "fetching" && updateState.isLoading && (
        <ContainerLoading />
      )}
      {updateState.error && updateState.status === "fetchFailed" ? (
        <BlockErrorMessage error={updateState.error} />
      ) : (
        <>
          <ModuleHeader
            moduleTitle={`Update ${moduleConfig.singleItemTitle}`}
            moduleUrl={moduleConfig.url}
          ></ModuleHeader>

          {Object.keys(formInitialValues).length > 0 &&
            updateState.data &&
            updateState.data.item &&
            !isEmpty(updateState.data.item._id) && (
              <section className="content">
                <Card>
                  <Card.Body>
                    {updateState.isLoading && (
                      <div className="overlay position-absolute containerLoading">
                        <i className="fas fa-2x fa-sync-alt fa-spin"></i>
                      </div>
                    )}
                    <Formik
                      initialValues={{
                        ...formInitialValues,
                        file: updateState.data.item.image,
                        slideImageFile: updateState.data.item.slideImage,
                      }}
                      validationSchema={yup.object({
                        title: yup
                          .string()
                          .required("Title is required."),
                        description: yup
                          .string()
                          .required("Description is required."),
                        file: yup
                          .mixed()
                          .required("Please upload an image.")
                          .imageTypeCheck(
                            moduleConfig.imageRequirement.allowedExtensions
                          )
                          .imageSizeCheck(moduleConfig.imageRequirement.size),
                        // slideBackgroundColor: yup
                        //   .string()
                        //   .required("Slide background color is required."),
                        // slideSectionBackgroundColor: yup
                        //   .string()
                        //   .required("Section background color is required."),
                        // slideSectionTextColor: yup
                        //   .string()
                        //   .required("Section text color is required."),
                        price: yup.number().required("Price is required."),
                        sortOrder: yup
                          .number()
                          .required("Sort order is required."),
                        status: yup.string().required("Status is required."),
                      })}
                      enableReinitialize={true}
                      validateOnChange={true}
                      validateOnBlur={true}
                      onSubmit={async (values, { resetForm }) => {
                        const formData = new FormData();
                        for (let name in values) {
                          if (Array.isArray(values[name])) {
                            formData.append(name, JSON.stringify(values[name]));
                          } else {
                            formData.append(name, values[name]);
                          }
                        }
                        await onSubmitGeneral(
                          formData,
                          `${moduleConfig.url}/${id}`,
                          moduleConfig.url,
                          updateDispatch,
                          navigate
                        );
                      }}
                    >
                      {(form) => (
                        <Form onSubmit={form.handleSubmit}>
                          {/* {console.log(form.errors)} */}
                          {updateState.error && (
                            <FormErrorMessage error={updateState.error} />
                          )}

                          <CMSFormInput
                            name="title"
                            label="Title"
                            required={true}
                            form={form}
                          />
                          <CMSFormRichText
                            name="description"
                            label="Description"
                            form={form}
                            required={true}
                          />

                          <CMSFormImageWithCrop
                            label="Image"
                            name="file"
                            info="Recommended dimensions 500*500"
                            helpMessage={{
                              message:
                                "Add image with transparent background & food item in center position for better visual experience",
                            }}
                            aspect={1 / 1}
                            outputSize={imageRequirement.dimension}
                            form={form}
                            required={true}
                          />

                          {/* <CMSFormColor
                            name="slideBackgroundColor"
                            label="Slide Background Color"
                            form={form}
                            required={true}
                          />

                          <CMSFormColor
                            name="slideSectionBackgroundColor"
                            label="Slide Section Background Color"
                            form={form}
                            required={true}
                          />

                          <CMSFormColor
                            name="slideSectionTextColor"
                            label="Slide Section Text Color"
                            form={form}
                            required={true}
                          /> */}

                          <CMSFormInput
                            name="price"
                            label="Price"
                            form={form}
                            required={true}
                          />

                          <CMSFormInput
                            name="sortOrder"
                            label="Sort Order"
                            form={form}
                            required={true}
                          />

                          <CMSFormSelect
                            name="status"
                            label="Status"
                            required={true}
                            form={form}
                            options={[
                              { label: "Select Status...", value: "" },
                              { label: "Enabled", value: "1" },
                              { label: "Disabled", value: "0" },
                            ]}
                          />
                          <button className="btn btn-primary" type="submit">
                            Submit
                          </button>
                        </Form>
                      )}
                    </Formik>
                  </Card.Body>
                </Card>
              </section>
            )}
        </>
      )}
    </DashboardLayout>
  );
};
export default MealUpdate;
